import { useState } from "react"
import { Button, Flex, Modal, Radio } from "antd"
import { useShallow } from "zustand/react/shallow"
import {
  AnalyticsAction,
  AudioWaveIcon,
  BackgroundMusicIcon,
  Text,
  useResourceUploadStatus,
} from "~/components"
import { useHotVideoStore, canUseHotVideoOriginalAudio } from "~/store/hotVideo"
import { ReferenceAudioField } from "../ReferenceAudioField"
import styles from "./index.css"

const SoundSettingsModal = ({ onClose }: { onClose: () => void }) => {
  const canUseOriginalAudio = useHotVideoStore(canUseHotVideoOriginalAudio)
  const {
    backgroundMusicMode,
    referenceAudio,
    setBackgroundMusicMode,
    setReferenceAudio,
  } = useHotVideoStore(
    useShallow((state) => ({
      backgroundMusicMode: state.backgroundMusicMode,
      referenceAudio: state.referenceAudio,
      setBackgroundMusicMode: state.setBackgroundMusicMode,
      setReferenceAudio: state.setReferenceAudio,
    })),
  )
  const [musicMode, setMusicMode] = useState(backgroundMusicMode)
  const [audio, setAudio] = useState(referenceAudio)
  const { pending, isPending } = useResourceUploadStatus()

  return (
    <Modal
      open
      centered
      title="视频声音设置"
      width={670}
      className={styles.modal}
      classNames={{
        container: styles.container,
        header: styles.header,
        body: styles.body,
        footer: styles.footer,
      }}
      onCancel={onClose}
      onOk={() => {
        if (isPending()) return
        setBackgroundMusicMode(musicMode)
        setReferenceAudio(audio)
        onClose()
      }}
      okText="确定"
      okButtonProps={{ disabled: pending }}
      footer={(_, { OkBtn }) => <OkBtn />}
    >
      <Flex
        vertical
        gap={16}
      >
        <Flex
          className={styles.section}
          vertical
          gap={12}
        >
          <Flex
            align="center"
            gap={8}
          >
            <BackgroundMusicIcon className={styles.sectionIcon} />
            <Text strong>背景音乐设置</Text>
          </Flex>
          <Radio.Group
            value={musicMode}
            orientation="vertical"
            onChange={(event) => {
              if (
                event.target.value === "generate" ||
                event.target.value === "original"
              )
                setMusicMode(event.target.value)
            }}
            options={[
              { label: "AI 自动生成新背景音乐", value: "generate" },
              {
                label: "沿用参考视频中的背景音乐",
                value: "original",
                disabled: !canUseOriginalAudio,
              },
            ]}
          />
          {!canUseOriginalAudio && (
            <Text
              type="secondary"
              size="s"
            >
              当前参考视频没有可用的原音轨，请使用 AI 自动生成背景音乐。
            </Text>
          )}
          {musicMode === "original" ? (
            <Text
              className={styles.hint}
              size="s"
            >
              仅参考视频无口播时，可沿用原背景音乐；若参考视频含口播，该选项不生效，AI
              将自动生成新背景音乐
            </Text>
          ) : null}
        </Flex>
        <div className={styles.section}>
          <ReferenceAudioField
            value={audio}
            onChange={setAudio}
          />
        </div>
      </Flex>
    </Modal>
  )
}

export const VideoSoundSettings = () => {
  const [open, setOpen] = useState(false)
  return (
    <>
      <AnalyticsAction
        action="视频声音设置"
        prefix="爆款复刻"
      >
        <Button
          className={styles.trigger}
          icon={<AudioWaveIcon className={styles.triggerIcon} />}
          onClick={() => setOpen(true)}
        >
          视频声音设置
        </Button>
      </AnalyticsAction>
      {open ? <SoundSettingsModal onClose={() => setOpen(false)} /> : null}
    </>
  )
}
